import { CommonModule } from '@angular/common';
import { Component, OnInit, TemplateRef } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IonicModule, PopoverController } from '@ionic/angular';
import { forkJoin, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpClientWithInFlightCache } from '../../global/httpClient';
import { ArmyBuilderConfig } from '../../global/config';
import { TooltipComponent } from '../form-controls/tooltip/tooltip.component';
import { InputAlert } from '../../global/input-alert/input-alert';
import { PageLayoutModule } from '../../global/page-layout/page-layout.module';
import { ToastService } from '../../global/toast/toast';
import { AlertService } from '../../global/alert/alert';
import { AdminEntityService } from '../entity/service/entity.service';

interface StatSummaryRow {
    gameId: string;
    entityType: string;
    count: number;
    liveCount: number;
}

interface StatSummary {
    total: number;
    liveTotal: number;
    summary: StatSummaryRow[];
}

@Component({
    selector: 'promote-data',
    standalone: true,
    imports: [CommonModule, IonicModule, PageLayoutModule, RouterLink],
    template: `
        <abs-page-layout [pageTitle]="'Promote Data'">
            <div class="content" *ngIf="stats">
                @if (stats) {
                    <table class="table">
                        <tr>
                            <th></th>
                            <th *ngIf="stats.staging">Staging</th>
                            <th>Live</th>
                        </tr>
                        @for (row of statRows; track row.key) {
                            <tr>
                                <th>
                                    {{ row.label }}
                                    <ion-icon
                                        class="info"
                                        name="information-circle"
                                        (click)="showDescription($event, row.description)"
                                    ></ion-icon>
                                </th>
                                <td *ngIf="stats.staging">
                                    <a class="stat-link" (click)="toggleStatSummary(row.key)">
                                        {{ stats.staging?.[row.stagingField] }}
                                        <ion-icon *ngIf="activeStatKey === row.key" name="chevron-up-outline" class="chevron"></ion-icon>
                                        <ion-icon *ngIf="activeStatKey !== row.key" name="chevron-down-outline" class="chevron"></ion-icon>
                                    </a>
                                </td>
                                <td>{{ stats.live?.[row.stagingField] }}</td>
                            </tr>
                            @if (activeStatKey === row.key && activeStatSummary) {
                                <tr class="breakdown-row">
                                    <td [attr.colspan]="stats.staging ? 3 : 2">
                                        <table class="breakdown-table">
                                            <tr>
                                                <th>Entity Type</th>
                                                <th>Staging</th>
                                                <th>Live</th>
                                                <th>Diff</th>
                                                @if (isActionable(row.key)) {
                                                    <th>Action</th>
                                                }
                                            </tr>
                                            @for (group of groupedStatSummary; track group.gameId) {
                                                <tr class="game-group-header">
                                                    <td>
                                                        <strong>{{ group.gameId }}</strong>
                                                    </td>
                                                    <td>{{ group.total }}</td>
                                                    <td>{{ group.liveTotal }}</td>
                                                    <td>{{ group.total - group.liveTotal }}</td>
                                                    @if (isActionable(row.key)) {
                                                        <td>
                                                            <ion-button
                                                                class="btn-xs"
                                                                color="warning"
                                                                [disabled]="actionInProgress"
                                                                (click)="executeGameAction(row.key, group.gameId, group.total)"
                                                            >
                                                                {{ getActionVerb(row.key) }} all {{ group.gameId }}
                                                            </ion-button>
                                                        </td>
                                                    }
                                                </tr>
                                                @for (item of group.rows; track item) {
                                                    <tr>
                                                        <td>{{ item.entityType }}</td>
                                                        <td>{{ item.count }}</td>
                                                        <td>{{ item.liveCount }}</td>
                                                        <td>
                                                            @if (item.count - item.liveCount > 0) {
                                                                <a
                                                                    [routerLink]="['/admin', item.gameId, 'entity', item.entityType]"
                                                                    [queryParams]="{ stat: row.key, diff: true }"
                                                                    >{{ item.count - item.liveCount }}</a
                                                                >
                                                            } @else {
                                                                0
                                                            }
                                                        </td>
                                                        @if (isActionable(row.key)) {
                                                            <td>
                                                                <ion-button
                                                                    class="btn-xs"
                                                                    [disabled]="actionInProgress"
                                                                    (click)="
                                                                        executeRowAction(row.key, item.gameId, item.entityType, item.count)
                                                                    "
                                                                >
                                                                    {{ getActionVerb(row.key) }}
                                                                </ion-button>
                                                            </td>
                                                        }
                                                    </tr>
                                                }
                                            }
                                            @if (activeStatSummary.summary.length === 0) {
                                                <tr>
                                                    <td [attr.colspan]="isActionable(row.key) ? 5 : 4">No entities found.</td>
                                                </tr>
                                            }
                                            <tr>
                                                <th>Total</th>
                                                <td>{{ activeStatSummary.total }}</td>
                                                <td>{{ activeStatSummary.liveTotal }}</td>
                                                <td>{{ activeStatSummary.total - activeStatSummary.liveTotal }}</td>
                                                @if (isActionable(row.key)) {
                                                    <th></th>
                                                }
                                            </tr>
                                        </table>
                                    </td>
                                </tr>
                            }
                        }
                    </table>

                    <div class="buttons vertical">
                        <ion-button [disabled]="!stats.staging?.betaCount" (click)="removeBeta()"
                            >Remove Beta role from {{ stats.staging?.betaCount }} entities</ion-button
                        >
                        <ion-button [disabled]="!stats.staging?.earlyAccessCount" (click)="promoteToGeneralAccess()"
                            >Remove {{ stats.staging?.earlyAccessCount }} entities from early access</ion-button
                        >
                        <ion-button [disabled]="!stats.staging?.missingAnnualCount" (click)="fixMissingAnnualRole()"
                            >Fix {{ stats.staging?.missingAnnualCount }} "Monthly Only" entities</ion-button
                        >
                        <ion-button *ngIf="stats.staging" [disabled]="!stats.staging?.entityCount" (click)="promoteToProduction()"
                            >Upsert {{ stats.staging?.entityCount }} entities to Production</ion-button
                        >
                        <ion-button *ngIf="stats.staging" (click)="promoteTranslations()">Promote Translations to Production</ion-button>
                        <ion-button *ngIf="stats.staging" (click)="promoteStoredFiles()">Promote Stored Files to Production</ion-button>
                        <ion-button (click)="clearCache()">Clear Cache (this environment only)</ion-button>
                    </div>
                } @else {
                    <ion-spinner></ion-spinner>
                }
            </div>
        </abs-page-layout>
    `,
    styles: [
        `
            table {
                margin: 20px 0;
                width: 100%;
            }
            tr {
                border-bottom: 1px solid #a4b0bc;
            }
            th {
                background: #22556a;
                font-weight: bold;
                color: #ffffff;
                border: 1px solid #fff;
            }
            td {
                background: #c4cad1;
                border: 1px solid #fff;
            }
            td.blue {
                background: #c4cad1 !important;
            }
            td:nth-child(even) {
                background: #f9e5c0;
            }
            td.yellow {
                background: #f9e5c0 !important;
            }
            td,
            th {
                padding: 5px;
                text-align: center;
            }
            .stat-link {
                cursor: pointer;
                color: #1a6b8a;
                text-decoration: underline;
                display: inline-flex;
                align-items: center;
                gap: 4px;
            }
            .chevron {
                font-size: 12px;
            }
            .breakdown-row td {
                padding: 0;
                background: transparent;
            }
            .breakdown-table {
                margin: 0;
                width: 100%;
            }
            .breakdown-table th {
                background: #3a7a8a;
            }
            .breakdown-table td {
                background: #dde3e8;
            }
            .game-group-header td {
                background: #b0c4ce;
                font-weight: bold;
            }
            .breakdown-table ion-button.btn-xs {
                min-height: 24px;
                height: 24px;
                font-size: 0.75em;
            }
        `
    ]
})
export class PromoteDataPage implements OnInit {
    stats: any;

    activeStatKey: string | null = null;
    activeStatSummary: StatSummary | null = null;
    groupedStatSummary: { gameId: string; total: number; liveTotal: number; rows: StatSummaryRow[] }[] = [];
    actionInProgress = false;

    private actionableStats = new Set(['beta', 'earlyAccess', 'missingAnnual', 'entity', 'stagingOnly']);
    private actionVerbs: Record<string, string> = {
        beta: 'Remove Beta',
        earlyAccess: 'Promote to GA',
        missingAnnual: 'Fix Annual Role',
        entity: 'Promote to Prod',
        stagingOnly: 'Promote to Prod'
    };

    statRows = [
        {
            key: 'entity',
            label: 'Promotable Entity Count',
            stagingField: 'entityCount',
            description: 'All entities that do not have the Beta role. These can be promoted to live, leaving the Beta entities in staging.'
        },
        {
            key: 'regularAccess',
            label: 'Generally Available',
            stagingField: 'regularAccessCount',
            description: 'Entities that are available to subscribers (monthly or annual).'
        },
        {
            key: 'earlyAccess',
            label: 'Early Access',
            stagingField: 'earlyAccessCount',
            description: 'Entities that are available to annual subscribers only.'
        },
        {
            key: 'missingAnnual',
            label: 'Missing Annual Subscriber Role',
            stagingField: 'missingAnnualCount',
            description:
                'Entities that are available to only monthly subscribers but not annual subscribers. There is no reason for this to occur, so this should probably be fixed with the button below.'
        },
        {
            key: 'noRoles',
            label: 'No Roles',
            stagingField: 'noRolesCount',
            description:
                'Entities that are available to any user, regardless of role. Generally only pages and products, but not game data.'
        },
        {
            key: 'beta',
            label: 'Beta',
            stagingField: 'betaCount',
            description: 'Entities that have the beta role. These entities will be skipped when promoting to production.'
        }
    ];

    constructor(
        private httpClient: HttpClientWithInFlightCache,
        private appConfig: ArmyBuilderConfig,
        private entityService: AdminEntityService,
        private toastService: ToastService,
        private alertService: AlertService,
        private popoverController: PopoverController,
        private alert: InputAlert
    ) {}

    ngOnInit() {
        this.refresh();
    }

    refresh() {
        this.httpClient.get(this.appConfig.apiBaseUrl + '/admin/promotion/stats').subscribe((res) => {
            this.stats = res;
        });
    }

    toggleStatSummary(statKey: string) {
        if (this.activeStatKey === statKey) {
            this.activeStatKey = null;
            this.activeStatSummary = null;
            this.groupedStatSummary = [];
            return;
        }
        this.activeStatKey = statKey;
        this.activeStatSummary = null;
        this.groupedStatSummary = [];
        this.entityService.getStatSummary(statKey).subscribe((res: any) => {
            this.activeStatSummary = res;
            this.groupedStatSummary = this.groupByGameId(res.summary);
        });
    }

    isActionable(statKey: string): boolean {
        return this.actionableStats.has(statKey);
    }

    getActionVerb(statKey: string): string {
        return this.actionVerbs[statKey] ?? '';
    }

    async executeRowAction(stat: string, gameId: string, entityType: string, count: number) {
        const verb = this.getActionVerb(stat);
        const { role } = await this.alertService.showConfirm(verb, `This will affect ${count} ${entityType} entities in ${gameId}`);
        if (role !== 'ok') return;
        this.actionInProgress = true;
        this.entityService.executeStatAction(stat, gameId, entityType).subscribe({
            next: () => {
                this.toastService.showToast('Success', `${verb}: ${gameId}/${entityType}`, { color: 'success', duration: 3000 });
                this.actionInProgress = false;
                this.refresh();
                this.toggleStatSummary(stat); // close
                this.toggleStatSummary(stat); // re-open to reload
            },
            error: () => {
                this.actionInProgress = false;
                this.toastService.showToast('Error', `Failed: ${verb} ${gameId}/${entityType}`, { color: 'danger', duration: 3000 });
            }
        });
    }

    async executeGameAction(stat: string, gameId: string, count: number) {
        const verb = this.getActionVerb(stat);
        const { role } = await this.alertService.showConfirm(verb, `All ${count} entities in ${gameId}`);
        if (role !== 'ok') return;
        this.actionInProgress = true;
        this.entityService.executeStatActionForGame(stat, gameId).subscribe({
            next: () => {
                this.toastService.showToast('Success', `${verb}: all ${gameId}`, { color: 'success', duration: 3000 });
                this.actionInProgress = false;
                this.refresh();
                this.toggleStatSummary(stat);
                this.toggleStatSummary(stat);
            },
            error: () => {
                this.actionInProgress = false;
                this.toastService.showToast('Error', `Failed: ${verb} all ${gameId}`, { color: 'danger', duration: 3000 });
            }
        });
    }

    private groupByGameId(rows: StatSummaryRow[]): { gameId: string; total: number; liveTotal: number; rows: StatSummaryRow[] }[] {
        const map = new Map<string, StatSummaryRow[]>();
        for (const row of rows) {
            const group = map.get(row.gameId) ?? [];
            group.push(row);
            map.set(row.gameId, group);
        }
        return Array.from(map.entries()).map(([gameId, items]) => ({
            gameId,
            total: items.reduce((sum, r) => sum + r.count, 0),
            liveTotal: items.reduce((sum, r) => sum + r.liveCount, 0),
            rows: items
        }));
    }

    async promoteToProduction() {
        const res = await this.alert.show(
            'Promote to Production',
            'Confirm that you want to promote all non-beta entities to production by typing PRODUCTION in caps below.',
            '',
            'text',
            true
        );

        if (res !== 'PRODUCTION') {
            this.toastService.showToast('Cancelled', 'You typed the wrong confirmation message.  No entities were promoted.', {
                color: 'danger',
                duration: 3000
            });
            return;
        }
        this.httpClient.post(this.appConfig.apiBaseUrl + '/admin/promotion/promoteToProd', {}).subscribe((res) => {
            console.log(res);
            this.toastService.showToast('Success', 'Promoted all non-beta entities to production', { color: 'success', duration: 3000 });
            this.refresh();
        });
    }

    removeBeta() {
        this.httpClient.post(this.appConfig.apiBaseUrl + '/admin/promotion/removeBeta', {}).subscribe((res) => {
            console.log(res);
            this.toastService.showToast('Success', 'Removed beta roles from all entities', { color: 'success', duration: 3000 });
            this.refresh();
        });
    }

    promoteToGeneralAccess() {
        this.httpClient.post(this.appConfig.apiBaseUrl + '/admin/promotion/promoteToGeneralAccess', {}).subscribe((res) => {
            console.log(res);
            this.toastService.showToast('Success', 'Promoted all entities to general access', { color: 'success', duration: 3000 });
            this.refresh();
        });
    }

    fixMissingAnnualRole() {
        this.httpClient.post(this.appConfig.apiBaseUrl + '/admin/promotion/fixMissingAnnualRole', {}).subscribe((res) => {
            console.log(res);
            this.toastService.showToast('Success', 'Fixed all "Monthly Only" entities', { color: 'success', duration: 3000 });
            this.refresh();
        });
    }

    async showDescription(e: Event, description: string) {
        const popover = await this.popoverController.create({
            component: TooltipComponent,
            event: e,
            componentProps: {
                description
            }
        });
        popover.present();
    }

    async promoteStoredFiles() {
        const { role } = await this.alertService.showConfirm(
            'Promote Stored Files',
            'This will copy all admin image metadata records from staging to production. The image files themselves are already shared via S3. Continue?'
        );
        if (role !== 'ok') return;
        this.httpClient.post(this.appConfig.apiBaseUrl + '/admin/promotion/promoteStoredFiles', {}).subscribe({
            next: (res: any) => {
                this.toastService.showToast('Success', `Promoted ${res.promoted} stored file record(s) to production`, {
                    color: 'success',
                    duration: 3000
                });
            },
            error: () => {
                this.toastService.showToast('Error', 'Failed to promote stored files', { color: 'danger', duration: 3000 });
            }
        });
    }

    async promoteTranslations() {
        const res = await this.alert.show(
            'Promote Translations to Production',
            `Confirm that you want to promote all translations (${(this.appConfig.translationGameIds ?? ['global']).join(', ')}) to production by typing PRODUCTION in caps below.`,
            '',
            'text',
            true
        );

        if (res !== 'PRODUCTION') {
            this.toastService.showToast('Cancelled', 'You typed the wrong confirmation message. No translations were promoted.', {
                color: 'danger',
                duration: 3000
            });
            return;
        }

        const gameIds = this.appConfig.translationGameIds ?? ['global'];
        const languages = this.appConfig.supportedLanguages ?? ['en'];
        const baseUrl = this.appConfig.apiBaseUrl;

        forkJoin(
            gameIds.flatMap((gameId) =>
                languages.map((lang) =>
                    this.httpClient.get(`${baseUrl}/translation/${gameId}/${lang}`).pipe(map((value) => ({ gameId, lang, value })))
                )
            )
        )
            .pipe(
                switchMap((translationSets) =>
                    forkJoin(
                        translationSets.map(({ gameId, lang, value }) =>
                            this.httpClient.post(`${baseUrl}/admin/promotion/translation/${gameId}/${lang}/promoteToProd`, value.value)
                        )
                    )
                )
            )
            .subscribe({
                next: () => {
                    this.toastService.showToast('Success', 'Promoted all translations to production', {
                        color: 'success',
                        duration: 3000
                    });
                },
                error: () => {
                    this.toastService.showToast('Error', 'Failed to promote translations to production', {
                        color: 'danger',
                        duration: 3000
                    });
                }
            });
    }

    clearCache() {
        this.httpClient.delete(this.appConfig.apiBaseUrl + '/library/cache', null, {}).subscribe((res) => {
            console.log(res);
            this.toastService.showToast('Success', 'Cleared cache', { color: 'success', duration: 3000 });
            this.refresh();
        });
    }
}
